import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  DestroyRef,
  Output,
  EventEmitter,
  input,
  output,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { FeatureFlag, NewPermission } from 'app/core/enums';
import { AuthService } from 'app/core/services/auth.service';
import { PipelineAddComponent } from 'app/modules/projects/modals/pipeline-add/pipeline-add.component';
import { Pipeline } from 'app/modules/projects/projects.types';
import { Pipelines } from 'app/state/pipelines/action';
import { PipelinesState } from 'app/state/pipelines/state';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { TsDividerComponent, TSIconModule, TSTreeAction, TSTreeMenuModule, TSTreeNode } from 'tsui';
import { UserState } from '@state/user/state';
import { UserService } from 'app/layout/common/user/user.service';
import { AccountState } from '@state/account/state';
import { AsyncPipe } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-deals-sidebar',
  standalone: true,
  imports: [TSTreeMenuModule, AsyncPipe, TsDividerComponent, TSIconModule, LucideAngularModule],
  templateUrl: './deals-sidebar.component.html',
  styleUrls: ['./deals-sidebar.component.scss'],
})
export class DealsSidebarComponent implements OnInit {
  @Output() sidebarToggle = new EventEmitter<void>();
  onNavigate = output<void>();
  showRecent = input<boolean>(false);
  userService = inject(UserService);

  pipelines: Pipeline[];
  viewsGroups: any[];
  selectedPipeline: Pipeline;
  currentUrl: string;
  loading = true;

  readonly router = inject(Router);
  private store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  pipelines$: Observable<Pipeline[]> = inject(Store).select(PipelinesState.getPipelines);
  pipelineGroups$: Observable<any[]> = inject(Store).select(PipelinesState.getPipelinesGroups);

  tsTreeActions: TSTreeAction[] = [];
  sidebarItems: TSTreeNode[] = [];

  recentDeals$ = this.userService.getRecentProjects(this.store.selectSnapshot(AccountState.getAccount)?.id).pipe(
    map((projects) =>
      projects.map((project) => {
        return {
          id: project.id.toString(),
          name: project.name,
          icon: 'ts_clock-five',
          link: `/projects/${project.pipeline_id}/${project.id}`,
        };
      }),
    ),
  );

  constructor(
    public matDialog: MatDialog,
    private readonly authService: AuthService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  canManagePipeline(): boolean {
    return this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_PUBLIC_CUSTOM_VIEWS));
  }

  ngOnInit() {
    if (this.canManagePipeline()) {
      this.tsTreeActions = [
        {
          key: 'new-group',
          label: 'Add new group',
          icon: 'add',
        },
        {
          key: 'project-sidebar-settings',
          label: 'Settings',
          icon: 'settings',
        },
        {
          key: 'collapse-sidebar',
          label: 'Collapse',
          icon: 'ts_side-menu',
        },
      ];

      if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_NAVIGATION))) {
        this.tsTreeActions = this.tsTreeActions.filter((action) => action.key !== 'collapse-sidebar');
      }
    }

    this.store.dispatch([Pipelines.Get, Pipelines.GetGroups]);

    this.currentUrl = this.router.url.split('?')[0];

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url.split('?')[0];
        this.setSidebarItems();
        this.navigateToPipeline();
      }
    });

    combineLatest([this.pipelines$, this.pipelineGroups$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([pipelines, groups]) => {
          return Array.isArray(pipelines) && Array.isArray(groups) && !!pipelines && !!groups;
        }),
      )
      .subscribe(([pipelines, groups]) => {
        this.pipelines = pipelines || [];
        this.viewsGroups = groups || [];
        this.setSidebarItems();
        this.navigateToPipeline();
      });
  }

  private setSidebarItems(): void {
    this.sidebarItems = [
      ...(this.viewsGroups?.map((viewGroup) => {
        const filteredPipelines = this.pipelines?.filter((p) => {
          return viewGroup.members.find((member) => member.groupable_id == p.id) !== undefined;
        });

        const sidebarItem: TSTreeNode = {
          id: +viewGroup.id,
          name: viewGroup.label,
          expandable: true,
          children: filteredPipelines.map((pipeline) => this.getSidebarMenuItem(pipeline)),
        };

        return sidebarItem;
      }) || []),
    ];

    const items = this.pipelines
      ?.filter((p) => !p.object_group_id)
      ?.map((pipeline) => this.getSidebarMenuItem(pipeline));

    if (items?.length) this.sidebarItems.push(...items);
    this.loading = false;
    this.changeDetector.detectChanges();
  }

  private openAddPipelineModal(parent): void {
    let modalData;

    if (parent) {
      modalData = { pipelines: this.pipelines, creatingView: true, selectedViewGroup: parent };
    } else {
      modalData = { pipelines: this.pipelines, creatingGroup: true };
    }

    this.matDialog
      .open(PipelineAddComponent, {
        data: modalData,
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => this.store.dispatch([Pipelines.Get, Pipelines.GetGroups]));
  }

  onNewItem(action: TSTreeAction): void {
    this.openAddPipelineModal(action.meta.parent);
  }

  onActionClicked(action: TSTreeAction): void {
    const actionMap = {
      'new-item': () => this.openAddPipelineModal(action.meta.parent),
      'new-group': () => this.openAddPipelineModal(null),
      'project-sidebar-settings': () => this.router.navigateByUrl('/projects/settings'),
      'collapse-sidebar': () => this.toggleSidebar(),
    };

    actionMap[action.key] && actionMap[action.key]();
  }

  private getSidebarMenuItem(pipeline: Pipeline): TSTreeNode {
    return {
      id: pipeline.id.toString(),
      name: pipeline.name,
      badge: {
        value: pipeline.num_projects_user_has_access_to || pipeline.num_projects,
        type: 'transparent',
      },
      icon: pipeline.pipeline_type === 'static' ? 'list_alt' : 'low_priority',
      iconTooltip: pipeline.pipeline_type === 'static' ? 'Static' : 'Dynamic',
      link: '/projects/' + pipeline.id,
    };
  }

  checkViewIsActive = (node): boolean => {
    return !!node.link && this.currentUrl.startsWith(node.link);
  };

  private navigateToPipeline(): void {
    if (this.currentUrl === '/projects') {
      const lastAccessedPipeline = localStorage.getItem('projectPipeline');
      if (lastAccessedPipeline) {
        this.router.navigateByUrl('/projects/' + lastAccessedPipeline);
        return;
      }
      const firstPipeline = this.pipelines.sort((a, b) => a.id - b.id).find((p) => p.pipeline_type === 'static');
      if (firstPipeline) {
        this.router.navigateByUrl('/projects/' + firstPipeline.id);
      }
    }
  }

  toggleSidebar(): void {
    this.sidebarToggle.emit();
  }

  delegateLeafClickFn(route: string): void {
    this.router.navigateByUrl(route);
    this.onNavigate.emit();
  }
}
